import { Text } from '@betterment/samba-react';
import {
  Box,
  Button,
  ButtonProps,
  CloseButton,
  Flex,
  Heading,
  ModalContent,
  ModalOverlay,
  get as themeGet,
  useTheme,
} from '@betterment/style-closet-react';
import styled from '@emotion/styled';

import { trackClick } from '../../features/track';
import { ContentCardProps } from '../data';

interface TrackableButtonProps extends ButtonProps {
  href?: string;
  target?: string;
  rel?: string;
}

const TrackableButton = styled(Button)<TrackableButtonProps>`
  text-decoration: none;
`;

const MessageOverlay = styled(ModalOverlay)`
  z-index: ${themeGet('zIndexes.overlay')};
`;

const OverlayNotice: React.FC<ContentCardProps> = ({
  title,
  description,
  linkText,
  imageUrl,
  url,
  dismissible,
  handleDismissal,
  handleCardClick,
  trackingModule: eventModule,
  trackingFlow: eventFlow,
  extras: { name: eventName, imageAltText, linkTarget },
}) => {
  const { theme } = useTheme();
  const hasMainAction = url && linkText;

  if (!dismissible || !handleDismissal) return null;

  return (
    <MessageOverlay
      onDismiss={() => handleDismissal()}
      alignItems={['flex-end', 'center']}
      justifyContent="center"
      py={[0, '10vh']}
    >
      <ModalContent width="62.4rem" aria-label={title}>
        <CloseButton closeHandler={handleDismissal} />
        {imageUrl && (
          <Box mb="6" style={{ lineHeight: 0 }}>
            <img src={imageUrl} alt={imageAltText || title} width="100%" height="100%" />
          </Box>
        )}
        <Box p="9">
          <Heading variant="secondary">{title}</Heading>
          <Box>
            <Text
              dangerouslySetInnerHTML={{ __html: description }}
              color="text.weak"
              css={{
                marginBottom: 'space.0',
              }}
            />
          </Box>
          <Flex mt="6" alignItems="center">
            {hasMainAction && (
              <TrackableButton
                as="a"
                style={{ color: theme.colors.bmtWhite, marginRight: theme.space[6] }}
                href={url}
                target={linkTarget}
                rel="noopener noreferrer"
                data-testid="contentCardLink"
                onClick={e => {
                  trackClick({
                    name: `${eventName}_${linkText}`,
                    module: eventModule,
                    flow: eventFlow,
                  });
                  handleCardClick?.(e);
                }}
              >
                {linkText}
              </TrackableButton>
            )}

            <Button
              onClick={handleDismissal}
              variant={hasMainAction ? 'secondary' : 'primary'}
              aria-label="Close modal"
            >
              Close
            </Button>
          </Flex>
        </Box>
      </ModalContent>
    </MessageOverlay>
  );
};

export default OverlayNotice;
